import React, { useMemo, useState, useEffect } from 'react';
// Force rebuild

const ShareResultsModal = ({ isOpen, onClose, players, handHistory, gameConfig }) => {
    const MAX_CHARS = 500; // Increased for formatted text

    const sortedPlayers = useMemo(() => {
        if (!players) return [];
        return [...players].sort((a, b) => b.score - a.score);
    }, [players]);

    const winner = sortedPlayers[0];

    const shareText = useMemo(() => {
        if (!winner) return '';

        const date = new Date().toLocaleDateString();
        let text = `♠️ *Cutthroat Spades Results* ♠️\n📅 ${date}\n\n`;

        text += `🏆 *Winner: ${winner.name}* (${winner.score} pts)\n\n`;
        text += `📊 *Standings:*\n`;

        sortedPlayers.forEach((p, i) => {
            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : '•';
            text += `${medal} ${p.name}: ${p.score}\n`;
        });

        text += `\nPlayed ${(handHistory || []).length} hands. What a game! 🔥`;
        return text;
    }, [sortedPlayers, winner, handHistory]);

    const [text, setText] = useState(shareText);
    const isOverLimit = text.length > MAX_CHARS;

    useEffect(() => {
        if (isOpen) {
            setText(shareText);
        }
    }, [isOpen, shareText]);



    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(text);
            // Visual feedback could be added here, but alert is safe for now
            alert('Results copied to clipboard! 📋');
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center z-50 p-4 animate-in fade-in duration-200" onClick={onClose}>
            <div
                className="w-full max-w-md bg-[#1e293b] rounded-3xl border border-white/10 shadow-2xl flex flex-col max-h-[90vh] overflow-hidden transform transition-all scale-100"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 pb-4 border-b border-white/5 bg-gradient-to-b from-white/5 to-transparent">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/20 text-amber-400 mb-3 ring-1 ring-amber-500/50 shadow-[0_0_15px_rgba(245,158,11,0.3)]">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" /></svg>
                        </div>
                        <h1 className="text-2xl font-black tracking-tight font-['Roboto_Slab'] text-white mb-1">
                            Share Results
                        </h1>
                        <p className="text-gray-400 text-sm font-medium">
                            Show off your victory!
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 overflow-y-auto">
                    {/* Share Buttons Grid */}
                    <div className="grid grid-cols-4 gap-3">
                        {[
                            {
                                name: 'WhatsApp',
                                color: 'bg-[#25D366]',
                                hover: 'hover:bg-[#20bd5a]',
                                href: `https://wa.me/?text=${encodeURIComponent(text)}`,
                                icon: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            },
                            {
                                name: 'Message',
                                color: 'bg-[#3B82F6]',
                                hover: 'hover:bg-[#2563eb]',
                                href: `sms:?&body=${encodeURIComponent(text)}`,
                                icon: <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                            },
                            {
                                name: 'Twitter',
                                color: 'bg-[#1DA1F2]',
                                hover: 'hover:bg-[#1a8cd8]',
                                href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`,
                                target: '_blank',
                                icon: <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                            },
                            {
                                name: 'Copy',
                                color: 'bg-[#475569]',
                                hover: 'hover:bg-[#334155]',
                                onClick: handleCopyLink,
                                icon: <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                            }
                        ].map((btn) => {
                            const Component = btn.href ? 'a' : 'button';
                            return (
                                <Component
                                    key={btn.name}
                                    href={btn.href}
                                    target={btn.target}
                                    onClick={btn.onClick}
                                    className={`group flex flex-col items-center justify-center gap-2 p-3 rounded-2xl bg-white/5 border border-white/10 ${btn.hover} transition-all duration-200 hover:scale-105 hover:border-transparent no-underline`}
                                >
                                    <div className={`w-10 h-10 rounded-full ${btn.color} flex items-center justify-center text-white shadow-lg`}>
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            {btn.icon}
                                        </svg>
                                    </div>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 group-hover:text-white transition-colors">
                                        {btn.name}
                                    </span>
                                </Component>
                            );
                        })}
                    </div>

                    {/* Preview Box */}
                    <div className="relative group">
                        <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-purple-500/20 rounded-2xl blur opacity-50 group-hover:opacity-75 transition duration-200"></div>
                        <div className="relative">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                className="w-full rounded-xl bg-[#0f172a] border border-white/10 p-4 text-sm text-gray-300 font-mono resize-none focus:outline-none focus:border-amber-500/50 transition-colors"
                                rows="6"
                                maxLength={MAX_CHARS}
                                placeholder="Add a message..."
                            />
                            <div className="absolute bottom-3 right-3 flex items-center gap-2">
                                {isOverLimit && (
                                    <span className="text-xs text-red-400 font-bold animate-pulse">
                                        Too long!
                                    </span>
                                )}
                                <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${isOverLimit ? 'bg-red-500/20 text-red-400' : 'bg-white/5 text-gray-500'}`}>
                                    {text.length}/{MAX_CHARS}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 pt-0">
                    <button
                        onClick={onClose}
                        className="w-full py-3.5 rounded-xl bg-white/5 border border-white/10 text-gray-300 font-bold hover:bg-white/10 hover:text-white hover:border-white/20 active:scale-[0.98] transition-all duration-200"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ShareResultsModal;
